import { Component, OnInit, ViewChild } from '@angular/core';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { DateParserService } from '../../../services/daily-log/daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import {
  DailyLogOperationInstance,
  DailyLogSummaryModel,
  OperationClassificationRowDetails,
  TypeSwitch
} from '../../../models/projects/operation.models';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { ValidationResult } from '../../../models/projects/validation.models';
import { DateService } from '../../../services/date/date.service';
import { cloneDeep } from 'lodash';
import { DailyLogDatePickerComponent } from '../../shared/components/daily-log-date-picker/daily-log-date-picker';

@Component({
  selector: 'app-daily-log-summary-component',
  templateUrl: 'daily-log-summary-component.component.html'
})
export class DailyLogSummaryComponent implements OnInit {
  @ViewChild('dailyLogDatePickerComponent') dailyLogDatePickerComponent: DailyLogDatePickerComponent;

  // Validation variables
  validationResult: ValidationResult = new ValidationResult();
  validationLoading: boolean;
  tableOneTotalDuration: number;
  tableTwoTotalDuration: number;

  constructor(
    private appDataSyncService: AppDataSyncService,
    public dailyLogService: DailyLogService,
    private operationService: OperationService,
    public dateParserService: DateParserService,
    private dateService: DateService) {
  }

  loading: boolean;
  initialLoad: boolean;
  firstLoadComplete: boolean = false;
  dailyLogDate: PtLuxonDate = new PtLuxonDate();
  dailyLogData: DailyLogOperationInstance[];
  data: DailyLogSummaryModel[] = [];

  propertiesToGroupByTableOne = ['projectSpecificOperation', 'operationId'];
  propertiesToGroupByTableTwo = ['classificationName', 'subClassificationName', 'operationId'];
  propertiesToSum = ['duration', 'concurrentDurationModifier'];
  tableOneSummaryData: any;
  tableTwoSummaryData: any;
  tableOneGroupedData: any;
  tableTwoGroupedData: any;

  uniqueCategoryTags: string[] = [];
  uniqueClassification: string[] = [];
  uniqueSubClassification: string[] = [];

  dateBeforeInitialSetup: boolean;

  private static tableTwoAggregateFunction(data: Array<any>) {
    return data.reduce((r, o) => {
      // Our multi property key
      let key = o.classificationName + '_total';
      r[key]
        ? (r[key].duration += o.duration)
        : (r[key] = {...o});
      (r[key].duration += o.concurrentDurationModifier);

      key = o.classificationName + o.subClassificationName + '_total'; // combine these in a known format.
      r[key]
        ? (r[key].duration += o.duration)
        : (r[key] = {...o});

      (r[key].duration += o.concurrentDurationModifier);

      return r;
    }, {});
  }

  private static BuildKeyForRecord(item: any, properties: Array<string>): Array<any> {
    const result = [];
    for (let i = 0; i < properties.length; i++) {
      result.push(item[properties[i]]);
    }
    return result;
  }

  private static tableOneAggregateFunction(data: Array<any>) {
    return data.reduce((r, o) => {
      // Our multi property key
      let key = o.projectSpecificOperation;
      r[key]
        ? (r[key].duration += o.duration)
        : (r[key] = {...o});

      key = o.projectSpecificOperation + o.operationId; // combine these in a known format.
      r[key]
        ? (r[key].duration += o.duration)
        : (r[key] = {...o});

      // Single property key (in same loop)
      key = o.projectSpecificOperation + '_total';
      r[key]
        ? (r[key].duration += o.duration)
        : (r[key] = {...o});

      // Second single property key (in same loop)
      key = o.operationId + '_total';
      r[key]
        ? (r[key].duration += o.duration)
        : (r[key] = {...o});
      return r;
    }, {});
  }

  async ngOnInit() {
    this.loading = true;
    this.initialLoad = true;
    await this.ionViewEnter();
  }

  async ionViewEnter() {
    this.loadData(true);
  }

  clearData() {
    this.data = [];
    this.tableOneTotalDuration = 0;
    this.tableTwoTotalDuration = 0;
    this.uniqueCategoryTags = [];
    this.uniqueClassification = [];
    this.uniqueSubClassification = [];
    this.tableOneSummaryData = undefined;
    this.tableTwoSummaryData = undefined;
    this.tableOneGroupedData = undefined;
    this.tableTwoGroupedData = undefined;
  }

  async loadData(refresh: boolean) {
    this.loading = true;
    this.clearData();

    this.firstLoadComplete = false;
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();

    if (refresh) {
      await this.appDataSyncService.updateAppDataForDate(this.dailyLogDate);
    }

    const maxDate = (await this.dateService.getEndOfDayLocally(this.dailyLogDate.dateTime)).dateTime;

    await this.operationService.getAllProjectSpecificOperationInstancesForDay(this.dailyLogDate).then(async data => {
      // await this.trimOperationStartTimes(data);
      this.clearData();

      this.dailyLogData = data;

      for (let j = 0; j < this.dailyLogData.length; j++) {
        this.sortSwitchesChronologically(this.dailyLogData[j]);
        const dailyLogSummary = new DailyLogSummaryModel(this.dailyLogData[j], this.dailyLogDate.dateTime, maxDate);

        if (dailyLogSummary.duration > 0) {
          this.data.push(dailyLogSummary);
        }
      }

      // ensure that this happens before we set category tags or the tags may be wrong
      let tableOneData = cloneDeep(this.data.filter(value => !value.isConcurrentOperation || value.hasContractualSwitches));
      tableOneData = this.removeConcurrentSwitchDurationFromTotalDuration(tableOneData);

      this.setTable1Categories(this.data);

      this.tableOneSummaryData = DailyLogSummaryComponent.tableOneAggregateFunction(tableOneData);
      this.tableTwoSummaryData = DailyLogSummaryComponent.tableTwoAggregateFunction(this.data);

      this.tableOneGroupedData = this.Table1GroupByFunction(tableOneData, this.propertiesToGroupByTableOne, this.propertiesToSum);
      this.tableTwoGroupedData = this.Table2GroupByFunction(this.data, this.propertiesToGroupByTableTwo, this.propertiesToSum);

      let tableOneCount = 0;
      if (this.tableOneGroupedData) {
        const keys = Object.keys(this.tableOneGroupedData);

        keys.forEach(value => {
          tableOneCount += this.tableOneGroupedData[value].duration;
        });
      }

      let tableTwoCount = 0;
      if (this.tableTwoGroupedData) {
        const keys = Object.keys(this.tableTwoGroupedData);

        keys.forEach(value => {
          tableTwoCount += this.tableTwoGroupedData[value].duration;
        });
      }

      this.tableOneTotalDuration = tableOneCount;
      this.tableTwoTotalDuration = tableTwoCount;
      this.loading = false;
      this.initialLoad = false;
      this.firstLoadComplete = true;
    });
  }

  getOperationListForContractCategory(group: string): string[] {
    return Object.keys(this.tableOneGroupedData).filter(value => value.startsWith('["' + group + '",'));
  }

  getOperationListForClassificationAndSubClassification(className: string, subclass: string): string[] {
    return Object.keys(this.tableTwoGroupedData).filter(value => value.startsWith('["' + className + '","' + subclass + '",'));
  }

  getOperationListForClassification(classificationName: string): string[] {
    return Object.keys(this.tableTwoGroupedData).filter(value => value.startsWith('["' + classificationName));
  }

  getSubClassListForClassification(classification: any): OperationClassificationRowDetails[] {
    const classList = [];

    this.uniqueSubClassification.forEach(value => {
      if (this.tableTwoSummaryData[classification + value + '_total']) {
        const detail = new OperationClassificationRowDetails();
        const operationsForClassification = this.getOperationListForClassification(classification);
        const operationsForSubClassification = this.getOperationListForClassificationAndSubClassification(classification, value);

        if (operationsForSubClassification && operationsForClassification) {
          const percentage = operationsForSubClassification.length / operationsForClassification.length;
          detail.height = percentage * 100;
        }

        detail.name = value;
        classList.push(detail);
      }
    });

    return classList;
  }

  private Table1GroupByFunction(data: Array<any>, properties: Array<string>, propertiesToSum: Array<string>) {
    return data.reduce((r, o) => {
      const group = JSON.stringify(DailyLogSummaryComponent.BuildKeyForRecord(o, properties));
      if (r[group]) {
        r[group].records.push(o);
        for (let i = 0; i < propertiesToSum.length; i++) {
          r[group][propertiesToSum[i]] += o[propertiesToSum[i]];
        }
      } else {
        r[group] = {records: []};
        r[group].records.push(o);
        for (let i = 0; i < propertiesToSum.length; i++) {
          r[group][propertiesToSum[i]] = o[propertiesToSum[i]];
        }
      }
      return r;
    }, {});
  }

  private Table2GroupByFunction(data: Array<any>, properties: Array<string>, propertiesToSum: Array<string>) {
    return data.reduce((r, o) => {
      const group = JSON.stringify(DailyLogSummaryComponent.BuildKeyForRecord(o, properties));
      if (r[group]) {
        r[group].records.push(o);
        for (let i = 0; i < propertiesToSum.length; i++) {
          r[group][propertiesToSum[i]] += (o[propertiesToSum[i]] + o.concurrentDurationModifier);
        }
      } else {
        r[group] = {records: []};
        r[group].records.push(o);
        for (let i = 0; i < propertiesToSum.length; i++) {
          r[group][propertiesToSum[i]] = (o[propertiesToSum[i]] + o.concurrentDurationModifier);
        }
      }
      return r;
    }, {});
  }

  getTotalDuration() {
    let count = 0;
    if (this.tableOneGroupedData) {
      const keys = Object.keys(this.tableOneGroupedData);


      keys.forEach(value => {
        count += this.tableOneGroupedData[value].duration;
      });
    }
    return count;
  }

  async trimOperationStartTimes(operations: DailyLogOperationInstance[]) {
    for (let i = 0; i < operations.length; i++) {
      if (operations[i].startTime.dateTime < this.dailyLogDate.dateTime) {
        operations[i].startTime.dateTime = this.dailyLogDate.dateTime;
      }
    }
  }

  removeConcurrentSwitchDurationFromTotalDuration(data: DailyLogSummaryModel[]): DailyLogSummaryModel[] {
    data.forEach(summaryModel => {
      if (summaryModel.concurrentSwitchDuration) {
        summaryModel.duration -= summaryModel.concurrentSwitchDuration;
      }
    });

    // after removing current switch duration - check that there are no ops that are now 0 duration
    data = this.removeZeroDurationOperations(data);

    return data;
  }

  dateBeforeInitialSetupChanged($event: boolean) {
    this.dateBeforeInitialSetup = $event;
  }

  goToActiveLogDay() {
    this.dailyLogDatePickerComponent.goToActiveLogDay();
  }

  dateLoadingStarted() {
    this.firstLoadComplete = false;
  }

  dateLoadingFinished() {
    this.firstLoadComplete = true;
  }

  private sortSwitchesChronologically(operation: DailyLogOperationInstance) {
    if (!operation.switchTimes || operation.switchTimes.length < 2) {
      return;
    }

    operation.switchTimes.sort((a: TypeSwitch, b: TypeSwitch) => this.getSwitchTimeMillis(a) - this.getSwitchTimeMillis(b));
  }

  private getSwitchTimeMillis(typeSwitch: TypeSwitch): number {
    return typeSwitch && typeSwitch.date && typeSwitch.date.dateTime
      ? typeSwitch.date.dateTime.toMillis()
      : Number.MAX_VALUE;
  }

  private removeZeroDurationOperations(data: DailyLogSummaryModel[]) {
    const filteredData: DailyLogSummaryModel[] = [];

    for (let i = 0; i < data.length; i++) {
      if (data[i].duration > 0) {
        filteredData.push(cloneDeep(data[i]));
      }
    }

    return filteredData;
  }

  private setTable1Categories(tableOneData: DailyLogSummaryModel[]) {
    let flags = [];
    const l = tableOneData.length;
    let i;

    for (i = 0; i < l; i++) {
      if ((tableOneData[i].isConcurrentOperation && !tableOneData[i].hasContractualSwitches) || flags[tableOneData[i].projectSpecificOperation]) {
        continue;
      }
      flags[tableOneData[i].projectSpecificOperation] = true;
      this.uniqueCategoryTags.push(tableOneData[i].projectSpecificOperation);
    }

    flags = [];
    for (i = 0; i < l; i++) {
      if (flags[tableOneData[i].classificationName]) {
        continue;
      }
      flags[tableOneData[i].classificationName] = true;
      this.uniqueClassification.push(tableOneData[i].classificationName);
    }

    flags = [];
    for (i = 0; i < l; i++) {
      if (flags[tableOneData[i].subClassificationName]) {
        continue;
      }
      flags[tableOneData[i].subClassificationName] = true;
      this.uniqueSubClassification.push(tableOneData[i].subClassificationName);
    }
  }
}





// WEBPACK FOOTER //
// ./src/pages/daily-log/daily-log-summary-component/daily-log-summary-component.component.ts